<template>
    <div id="food-menu" class="food-page mobile-page-padding" ref="page-container">
        <!-- Product Customizer -->
        <div class="customizer-slot" v-if="hasProductType && !isLoading">
            <transition name="quick-fade">
                <router-view></router-view>
            </transition>
        </div>
        <div class="grid-x align-center content-padding">
            <div class="cell xxlarge-6 large-10 food-content-container">
                <mobile-header :title="storeName" ref="mobile-header">
                    <template v-slot:sticky>
                        <div v-if="searching" class="align-middle grid-x align-justify search-container">
                            <div class="cell shrink lens-icon">
                                <i v-html="Icon.SEARCH"></i>
                            </div>
                            <div class="cell auto">
                                <input class="search" type="text" @input="search = $event.target.value" :value="search" placeholder="Search menu" ref="search-input"/>
                            </div>
                            <div class="cell shrink">
                                <p class="cancel-search bold" @click="cancelSearch">Cancel</p>
                            </div>
                        </div>
                        <sliding-menu v-else :items="menuItems" :selectedItem="selectedSection" @select="selectSection"/>
                    </template>
                    <template v-slot:header-action>
                        <button class="search-button hide-for-large" @click="triggerSearch" v-html="Icon.SEARCH"></button>
                    </template>
                </mobile-header>
                <div class="grid-x align-center">
                    <div class="cell large-7 xxlarge-6 show-for-large desktop-search-container" :class="{focus: isSearchFocus}">
                        <div class="align-middle grid-x">
                            <div class="cell shrink lens-icon">
                                <i v-html="Icon.SEARCH"></i>
                            </div>
                            <div class="cell auto">
                                <input class="search display-small" type="text" @focus="isSearchFocus = true" @blur="isSearchFocus = false" @input="search = $event.target.value" :value="search" placeholder="Know exactly what you want? Search for it!" ref="search-input">
                            </div>
                            <div :class="['cancel-search', {active: search.length}]">
                                <a class="text-uppercase display-small bold color-dark-gray" @click.prevent="search = ''">Cancel</a>
                            </div>
                        </div>
                    </div>
                    <div class="cell show-for-large sliding-menu-container">
                        <sliding-menu id="tabs-menu" :items="menuItems" :selectedItem="selectedSection" @select="selectSection"/>
                    </div>
                    <div class="cell grid-x" id="menu-sections" v-if="productPages.length > 0">
                        <div class="cell menu-container" v-for="(productPage, i) in productPages" :key="productPage.id" :ref="`section-${i}`">
                            <div class="grid-container full">
                                <div :class="['section-title', 'grid-x', 'align-middle', {folded: isFolded(productPage.id)}]" @click="toggleFold(productPage.id)">
                                    <div class="cell auto">
                                        <h3 class="display-large semi-bold color-dark-gray margin-0">{{ productPage.name }}</h3>
                                        <p class="display-small page-description margin-0" v-if="productPage.description">{{ productPage.description }}</p>
                                        <p class="display-small page-description margin-0">
                                            {{ scheduleText(productPage.schedule) }}
                                        </p>
                                    </div>
                                    <span class="fold-icon cell shrink" v-html="(isFolded(productPage.id)) ? Icon.ARROW_RIGHT : Icon.ARROW_DOWN"></span>
                                </div>
                            </div>
                            <div :class="['grid-x', 'grid-margin-x', 'product-list', {unavailable: isPageUnavailable(productPage)}]" v-show="!isFolded(productPage.id)">
                                <template v-for="productType in productTypes(productPage)">
                                    <router-link v-for="product in productType.products"
                                        :key="product.id"
                                        :dusk="'product-link-' + product.id"
                                        :class="['cell', 'large-6', 'product-link', {'unavailable': !product.in_stock || isProductUnavailable(product)}]"
                                        :to="{ name: 'edit-product', params: { pageSlug: productPage.slug, productSlug: product.slug, productId: product.id, fromClick: true }}">
                                        <div class="grid-x">
                                            <div class="cell auto">
                                                <h4 class="display-regular semi-bold color-dark-gray">{{ product.name }}</h4>
                                                <p class="display-small color-battleship-gray product-description">{{ product.description }}</p>
                                                <p class="display-small color-dark-gray bold margin-0" v-if="product.in_stock">${{ parseFloat(product.price).toFixed(2) }}</p>
                                                <p class="display-small color-dark-gray bold margin-0" v-else>Out of stock</p>
                                                <p class="display-small page-description margin-0" v-if="product.schedule">{{ scheduleText(product.schedule) }}</p>
                                            </div>
                                            <div class="cell shrink" v-if="product.image">
                                                <img class="product-image lazy" :data-srcset="`${product.image.card} 1x, ${product.image.card_2x} 2x`" :alt="product.name + ' Image'">
                                            </div>
                                        </div>
                                    </router-link>
                                </template>
                                <template>
                                    <router-link v-for="product in productPage.products"
                                        :key="productPage.id + '_' + product.id"
                                        :dusk="'product-link-' + product.id"
                                        :class="['cell', 'large-6', 'product-link', {'unavailable': !product.in_stock || isProductUnavailable(product)}]"
                                        :to="{ name: 'edit-product', params: { pageSlug: productPage.slug, productSlug: product.slug, productId: product.id, fromClick: true}}">
                                        <div class="grid-x">
                                            <div class="cell auto">
                                                <h4 class="display-regular semi-bold color-dark-gray">{{ product.name }}</h4>
                                                <p class="display-small color-battleship-gray product-description">{{ product.description }}</p>
                                                <p class="display-small color-dark-gray bold margin-0" v-if="product.in_stock">${{ parseFloat(product.price).toFixed(2) }}</p>
                                                <p class="display-small color-dark-gray bold margin-0" v-else>Out of stock</p>
                                                <p class="display-small page-description margin-0" v-if="product.schedule">{{ scheduleText(product.schedule) }}</p>
                                            </div>
                                            <div class="cell shrink" v-if="product.image">
                                                <img class="product-image lazy" :data-srcset="`${product.image.card} 1x, ${product.image.card_2x} 2x`" :alt="product.name + ' Image'">
                                            </div>
                                        </div>
                                    </router-link>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <router-view></router-view>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import FoodModule from '@/stores/modules/food';
import { getModule } from 'vuex-module-decorators';
import Card from '@/components/front/ui/Card.vue';
import SlidingMenu from '@/components/front/ui/SlidingMenu.vue';
import MobileHeader from '@/components/front/ui/MobileHeader.vue';
import { Icon } from '@/front/icons';
import TimeSchedulerModule from '@/stores/modules/time-scheduler';
import { isProductUnavailable as isProductUnavailableUtil, isPageUnavailable as isPageUnavailableUtil, scheduleTextForToday } from '@/utils/availability';
import { Route } from 'vue-router';
import Settings, { SettingNamespace } from '@/utils/settings';
import ProductPage from '@/models/product-page';
import { registerScrollCallback } from '@/utils/scroll-event-manager';
import { updateLazyLoad } from '@/utils/utilities';
import { Availabilities } from '@/utils/types';

Component.registerHooks([
    'beforeRouteEnter'
]);

@Component({
    components: { MobileHeader, Card, SlidingMenu }
})
export default class FoodPage extends Vue
{
    foodModule!: FoodModule;
    timeSchedulerModule!: TimeSchedulerModule;
    foldedSections: number[] = [];
    selectedSection: number = -1;
    storeName: string = '';
    sectionPositions: number[] = [];
    searching: boolean = false;
    isSearchFocus: boolean = false;
    pagesSchedules: {id: number, schedule: Availabilities}[] = [];
    productsSchedules: {id: number, schedule: Availabilities}[] = [];
    timeSlotSize: number | null = null;

    async created() {
        this.foodModule = getModule(FoodModule, this.$store);
        this.timeSchedulerModule = getModule(TimeSchedulerModule, this.$store);

        const settings = await Settings.getInstance();
        this.storeName = settings.get(SettingNamespace.GENERAL, 'store_name')?.value;
        this.timeSlotSize = settings.get(SettingNamespace.ORDERS, 'time_slot_size')?.value ?? null;

        this.$watch('productPages', () => {
            this.$nextTick(this.updateSectionPositions);
            (<any>window).lazyLoadInstance.update();
        }, {immediate: true});
    }

    mounted() {
        registerScrollCallback('food', this.onScroll);
        updateLazyLoad();
    }

    beforeRouteEnter(to: Route, from: Route, next: Function) {
        next((vm: this) => {
            vm.updateRouteParameters(to.params.pageSlug, to.params.typeSlug);
        });
    }

    async updateRouteParameters(pageSlug: string | undefined, typeSlug: string | undefined) {
        if (pageSlug !== undefined) {
            this.foodModule.setProductPage(pageSlug);
        }
    }

    toggleFold(id: number) {
        const foldIndex = this.foldedSections.indexOf(id);
        if (foldIndex !== -1) {
            this.foldedSections.splice(foldIndex, 1);
        } else {
            this.foldedSections.push(id);
        }
        this.$nextTick(this.updateSectionPositions);
    }

    updateSectionPositions() {
        for (let i = 0; i < this.sectionsCount; i++) {
            if ((<HTMLElement[]>this.$refs[`section-${i}`])[0] !== undefined) {
                this.sectionPositions[i] = (<HTMLElement[]>this.$refs[`section-${i}`])[0].offsetTop;
            }
        }
    }

    onScroll(pageContainer: HTMLElement) {
        if ((<MobileHeader>this.$refs['mobile-header']) !== undefined) {
            const mobileHeader = (<MobileHeader>this.$refs['mobile-header']).$el;
            let currentSection: number = 0;

            for (let i = 0; i < this.sectionPositions.length; i++) {
                if (this.sectionPositions[i] < pageContainer.scrollTop + mobileHeader.getBoundingClientRect().height + 20) {
                    currentSection = i;
                }
            }
            this.selectedSection = currentSection;
        }
    }

    selectSection(index: number) {
        if (index >= 0) {
            this.selectedSection = index;
            const pageContainer = <HTMLElement>document.querySelector('.page-content-container');
            const foodContainer = <HTMLElement>this.$refs['page-container'];
            const sectionRef = (<HTMLElement[]>this.$refs[`section-${this.selectedSection}`]) ? (<HTMLElement[]>this.$refs[`section-${this.selectedSection}`])[0] : null;
            const mobileHeader = (<MobileHeader>this.$refs['mobile-header']).$el;
            const containerPadding = window.getComputedStyle(foodContainer, null).getPropertyValue('padding-top');

            let offset = 0;
            if (this.selectedSection > 0 && sectionRef) {
                offset = sectionRef.offsetTop + parseInt(containerPadding) - mobileHeader.getBoundingClientRect().height + 5;
            }

            pageContainer.scrollTo({
                top: offset,
                behavior: 'smooth'
            });

        }
    }

    cancelSearch() {
        this.search = '';
        this.searching = false;
        this.selectSection(0);
    }

    triggerSearch() {
        this.searching = true;
        this.$nextTick(() => {
            (<HTMLElement>this.$refs['search-input'])?.focus();
        });
    }

    set search(value: string) {
        this.foodModule.setSearch(value);
    }

    get search() {
        return this.foodModule.search;
    }

    get isFolded() {
        return (id: number) => {
            return this.foldedSections.includes(id);
        };
    }

    get productPages() {
        return this.foodModule.searchResults;
    }

    get productTypes() {
        return (productPage: ProductPage) => {
            return productPage.product_types;
        };
    }

    get sectionsCount() {
        return this.productPages.length;
    }

    get menuItems() {
        return this.productPages.map((productPage: ProductPage) => productPage.name);
    }

    get hasProductType() {
        return this.$route.params.typeSlug;
    }

    get isLoading() {
        return this.foodModule.isLoading;
    }

    get Icon() {
        return Icon;
    }

    get scheduleText() {
        return (schedules: Availabilities) => scheduleTextForToday(schedules, this.timeSlotSize, this.timeSchedulerModule.selectedTimeSlot);
    }

    get isProductUnavailable() {
        return (product: any) => isProductUnavailableUtil(product, this.timeSchedulerModule.selectedTimeSlot);
    }

    get isPageUnavailable() {
        return (page: any) => isPageUnavailableUtil(page, this.timeSchedulerModule.selectedTimeSlot);
    }
}
</script>
<style lang="scss">
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.food-page {
    @include breakpoint(large) {
        padding-top: 30px;
        padding-bottom: 150px;
    }

    .food-content-container {
        position: relative;
        max-width: 750px;
    }

    @include breakpoint(large) {
        .customizer-slot {
            position: relative;
            z-index: 10;
        }
    }
    .search-button {
        padding: 12.25px 14px;
        border-radius: 9999px;
        background-color: rgba(white, 0.1);
        svg {
            position: relative;
            top: 1.5px;
        }

        svg path {
            stroke: white;
        }
    }

    .search-container {
        background: var(--product-list-search-mobile-bg);

        .lens-icon {
            padding-left: 15px;
            padding-right: 5px;
            height: 18px;

            svg {
                path {
                    stroke: var(--product-list-search-mobile-fg);
                    fill: var(--product-list-search-mobile-fg);
                }
            }
        }

        .cancel-search {
            margin: 0;
            padding: 0 15px;
            text-transform: uppercase;
            letter-spacing: 0.05rem;
            color: var(--product-list-search-mobile-fg);
        }

        .search {
            padding-left: 10px;
            height: 45px;
            width: 100%;
            margin: 0;
            border: none;
            border-radius: 0;
            outline: none;
            appearance: none;
            box-shadow: none;

            &::placeholder {
                color: var(--battleship-gray);
            }
        }
    }

    .desktop-search-container {
        padding: 5px 20px;
        border-radius: var(--product-list-search-radius);
        border: var(--product-list-search-border);
        background-color: var(--product-list-search-bg);
        transition: background-color 0.3s ease, border-color 0.3s ease;

        &.focus {
            background-color: var(--product-list-search-focus-bg);
            border-color: var(--product-list-search-focus-border);

            .search {
                background-color: var(--product-list-search-focus-bg);
            }
        }

        .search {
            font-weight: 600;
            font-size: 16px;
            margin: 0;
            padding: 0;
            border: none;
            outline: none;
            appearance: none;
            box-shadow: none;
            color: var(--product-list-search-fg);
            transition: background-color 0.3s ease;

            &::placeholder {
                font-weight: 400;
                font-size: 13px;
                font-style: italic;
                color: var(--battleship-gray);
            }
        }

        .cancel-search {
            display: none;

            &.active {
                display: flex;
                align-items: center;
                padding-left: 5px;
            }
        }

        .lens-icon {
            height: 18px;
            margin: 0;
            padding-right: 10px;
        }
    }

    .sliding-menu-container {
        position: sticky;
        justify-content: flex-end;
        top: 74px;
        padding-top: 30px;
        z-index: 1;
        background: var(--sliding-menu-desktop-bg);
        border-bottom: var(--sliding-menu-desktop-border);
    }

    .menu-container {

        .section-title {
            padding: 15px 10px;
            margin-bottom: 20px;
            background: var(--product-list-section-bg);

            .page-description {
                color: var(--product-list-section-desc-color);
            }

            &.folded {
                margin-bottom: 5px;
            }

            .fold-icon {
                height: 30px;
            }

            &:hover {
                cursor: pointer;
            }
        }

        .product-list {

            @include breakpoint(medium down) {
                margin: 0;
            }

            &.unavailable {
                .product-link {
                    pointer-events: none;
                    cursor: not-allowed;
                    opacity: 0.7;
                }
            }

            .product-link {
                display: block;
                margin-bottom: 15px;
                padding: 5px 15px;
                transition: box-shadow 0.1s ease-in;
                &:hover {
                    @include breakpoint (large) {
                        box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.1202);
                    }
                }
                &.unavailable {
                    pointer-events: none;
                    cursor: not-allowed;
                    opacity: 0.7;
                }

                .product-description {
                    margin-bottom: 5px;
                }

                .product-image {
                    object-fit: cover;
                    width: 110px;
                    height: 110px;
                    margin-left: 15px;
                    border-radius: var(--product-list-image-radius);
                    background-color: #F1F2F4;
                }

                @include breakpoint(medium down) {
                    padding: 5px 0;
                }

                @include breakpoint(large up) {
                    border: var(--product-list-card-border);
                    border-radius: var(--product-list-card-border-radius);
                    padding: var(--product-list-card-padding);
                }
            }
        }

    }

}
</style>

